// Route A's manual fallback — the console's view of /admin/pathology/*
// (services/core/src/pathology/admin_api.py, queue.py, notice.py, referral_form.py).
// Shapes mirror what core returns; the decisions (placing, cancelling, refusing
// while a run holds the order) are core's, and the console only shows them.

/** A REQUESTED, Bioweave-paid order nobody has placed yet (queue.waiting). */
export type WaitingOrder = {
  order_id: string;
  user_ref: string;
  name?: string | null;
  provider?: string | null;
  panel?: string | null;
  created_at?: string | null;
  /** A recipe run's live claim: placing or cancelling is refused (409) until then. */
  claimed_until?: string | null;
};

/** An order a run handed to a person after pressing Order now (queue.held).
 *  A paid order may exist. ``ids_read`` are order ids the run READ and could not
 *  record: shown, never applied — only the clinic's Patients list says which is right. */
export type HeldOrder = {
  order_id: string;
  user_ref: string;
  name?: string | null;
  claim_id: string;
  held_at: string;
  ids_read: string[];
  panel?: string | null;
  created_at?: string | null;
};

/** A placed report core could not apply: a paid order at iMedical no record carries. */
export type UnmatchedPlacement = {
  at: string;
  provider_order_id: string;
  claim_id?: string | null;
  user_ref?: string | null;
  order_id?: string | null;
  refused: string;
};

/** Why a result is held (pathology.results HOLD_*). Structured, so the console
 *  never branches on the prose reason; absent until core sends it. */
export type ResultHoldKind = 'name_mismatch' | 'no_name' | 'ingest_failed';

/** A complete report the results poller would not ingest (results.held_results).
 *  Until a person releases it, that person's results never arrive. */
export type ResultHold = {
  order_id: string;
  user_ref: string;
  name?: string | null;
  panel?: string | null;
  status?: string | null;
  provider_order_id?: string | null;
  /** The check that failed, in words. Never the names compared. */
  reason: string;
  held_at: string;
  kind?: ResultHoldKind | null;
  /** The hold names the report it held, so a person can accept THAT report for
   *  the account. Core refuses an accept (409) when this is false. */
  can_accept_report: boolean;
};

/** The approver's waiting-orders email, last attempt (notice.json). */
export type LastNotice = {
  at: string;
  count: number;
  sent: boolean;
  error?: string | null;
  permanent?: boolean;
};

export type PathologyQueue = {
  waiting: WaitingOrder[];
  held: HeldOrder[];
  /** Optional until every core serving the console sends it. */
  results_held?: ResultHold[];
  unmatched_placements: UnmatchedPlacement[];
  last_notice: LastNotice | null;
};

/** referral_form.fill(): the checkout's fields in its order; ``value`` is null
 *  for a field with a problem. A problem stops the order and is never guessed. */
export type ReferralField = { key: string; label: string; value: string | null };
export type ReferralForm = {
  fields: ReferralField[];
  problems: string[];
  zone_id?: string | null;
  country_id?: string | null;
};
export type OrderReferral = { order: Record<string, unknown>; form: ReferralForm };

/** True while a recipe run's claim on the order is live: core refuses a hand
 *  placement or a cancel until it expires, because the run may press Order now. */
export function runHolds(o: Pick<WaitingOrder, 'claimed_until'>, now: Date = new Date()): boolean {
  if (!o.claimed_until) return false;
  const until = new Date(o.claimed_until).getTime();
  return Number.isFinite(until) && until > now.getTime();
}

export type NoticeState = 'none' | 'sent' | 'failed';

export function noticeState(n: LastNotice | null | undefined): NoticeState {
  if (!n) return 'none';
  return n.sent ? 'sent' : 'failed';
}

export const NOTICE_STATE_HEX: Record<NoticeState, string> = {
  none: '#8A94A6', sent: '#2BBAA8', failed: '#F2635A',
};

/** What a person should do about a held result, by its kind. A hold without a
 *  kind gets the general line rather than a guess from its reason text. */
export function resultHoldGuidance(kind: ResultHoldKind | null | undefined): string {
  switch (kind) {
    case 'name_mismatch':
      return 'Check the report against the person. If it is theirs (a middle name or title on it), accept it for this account; if the account\'s name is wrong, fix it and release.';
    case 'no_name':
      return 'Add the person\'s first and last name to the account, then release so the report is checked again.';
    case 'ingest_failed':
      return 'Ingesting the report failed three times. Release to try again.';
    default:
      return 'Check the report against the person, then accept it for this account or fix the cause and release.';
  }
}

/** An instant as local date + time, or an em dash. */
export function whenText(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** The person's name, or their user_ref when the account has none. */
export function whoText(o: { name?: string | null; user_ref?: string | null }): string {
  return (o.name ?? '').trim() || o.user_ref || '—';
}
